import {
	agentCanViewInteraction,
	isInteractionFiltered,
} from "@/common/helpers";
import { ServerEvent } from "@/common/notification";
import {
	InteractionFilterState,
	useInteractionFilterStore,
} from "@/common/stores/nonpersistingStores/interactionfilter";
import { QueuesState, useQueuesStore } from "@/common/stores/nonpersistingStores/queues";
import { notificationListener } from "@/hooks/notification";
import {
	CallSentimentChangedDto,
	InteractionDto,
	InteractionOperations,
	InteractionStatus,
	Queue,
	QueueChanged,
} from "@/repository/models";
import { fetchAllActiveInteractionsFn } from "@/repository/queryFns/supervisorInteractions";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

export const useActiveInteractions = () => {
	const queues = useQueuesStore((state: QueuesState) => state.queues);

	const queryClient = useQueryClient();

	const filterData = useInteractionFilterStore(
		(state: InteractionFilterState) => state.active
	);

	const supervisorActiveQueryKey = ["supervisorActiveInteractions", filterData];

	const fetchAllActiveInteractionsCallback = useCallback(
		(_: any) => {
			return fetchAllActiveInteractionsFn(filterData);
		},
		[filterData, supervisorActiveQueryKey]
	);

	const { data: activeInteractions } = useQuery<InteractionDto[]>({
		queryKey: supervisorActiveQueryKey,
		queryFn: (_: any) => fetchAllActiveInteractionsCallback(filterData),
		gcTime: 0,
		refetchOnMount: "always",
	});

	const updateActiveList = (interactionChanged: InteractionDto) => {
		if (
			!agentCanViewInteraction(queues, interactionChanged) &&
			interactionChanged.changeType !== InteractionOperations.Removed
		) {
			return;
		}

		switch (interactionChanged.changeType) {
			case InteractionOperations.Added:
				queryClient.setQueryData<InteractionDto[]>(
					supervisorActiveQueryKey,
					(oldData: InteractionDto[] | undefined) => {
						const existed = oldData?.find((x) => x.id == interactionChanged.id);

						if (!!existed) {
							return oldData?.map((x: InteractionDto) =>
								x.id === interactionChanged.id
									? { ...x, ...interactionChanged }
									: x
							);
						}

						return [interactionChanged, ...(oldData ?? [])];
					}
				);
				break;
			case InteractionOperations.Updated:
				updateActiveInteraction(interactionChanged);
				break;
			case InteractionOperations.Removed:
				removeActiveInteraction(interactionChanged);
				break;
			default:
				break;
		}
	};

	const removeActiveInteraction = (interactionRemoved: InteractionDto) => {
		if (
			interactionRemoved.status === InteractionStatus.WarmTransferInitialized
		) {
			return;
		}

		queryClient.setQueryData<InteractionDto[]>(
			supervisorActiveQueryKey,
			(oldData) => oldData?.filter((item) => item.id !== interactionRemoved.id)
		);
	};

	const updateActiveInteraction = (interactionChanged: InteractionDto) => {
		//#to fix issue: if user is hovering on status while interaction is changing, app crashes
		queryClient.setQueryData<InteractionDto[]>(
			supervisorActiveQueryKey,
			(oldData) => {
				return oldData?.map((item) =>
					item.id === interactionChanged.id
						? { ...item, ...interactionChanged }
						: item
				);
			}
		);
	};

	notificationListener<InteractionDto>(
		ServerEvent.SupervisedActiveInteractionChanged,
		(interaction: InteractionDto) => {
			if (interaction.status === InteractionStatus.Parked) {
				interaction.changeType = InteractionOperations.Removed;
			}

			if (!isInteractionFiltered(filterData, interaction)) {
				updateActiveList(interaction);
			}
		}
	);

	notificationListener(ServerEvent.UserMembershipChanged, (_: Queue) => {
		queryClient.invalidateQueries({ queryKey: supervisorActiveQueryKey });
	});

	notificationListener(ServerEvent.UserMembershipRemoved, (_: Queue) => {
		queryClient.invalidateQueries({ queryKey: supervisorActiveQueryKey });
	});

	notificationListener(ServerEvent.SupervisedAgentRefreshed, (_: Queue) => {
		queryClient.invalidateQueries({ queryKey: supervisorActiveQueryKey });
	});

	notificationListener<QueueChanged>(ServerEvent.QueueChanged, () => {
		queryClient.invalidateQueries({ queryKey: supervisorActiveQueryKey });
	});

	notificationListener<CallSentimentChangedDto>(
		ServerEvent.CallSentimentChanged,
		(notification: CallSentimentChangedDto) => {
			queryClient.setQueryData<InteractionDto[]>(
				supervisorActiveQueryKey,
				(oldData) =>
					oldData?.map((item) =>
						item.id === notification.sessionId
							? { ...item, overallSentiment: notification.overallSentiment }
							: item
					)
			);
		}
	);

	return { activeInteractions };
};
